import { useMemo } from 'react'

import { useTheme } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import { BIDS_PER_JOB_ANALYSIS_CHART_HEIGHT } from 'components/reports/Metrics/constants'

import { BAR_STYLE } from '../components/chartConfig'
import { BidsPerJobAnalysisBucket } from '../components/types'

interface Props {
  buckets: BidsPerJobAnalysisBucket[]
  height?: number
}

export const BidsPerJobAnalysisChart = ({
  buckets,
  height = BIDS_PER_JOB_ANALYSIS_CHART_HEIGHT,
}: Props) => {
  const theme = useTheme()
  const allJobsColor = theme.palette.primary.main
  const cxWonColor = theme.palette.success.main
  const opWonColor = theme.palette.warning.main

  const labels = useMemo(() => buckets.map((bucket) => bucket.label), [buckets])

  const data = useMemo<ChartData<'bar', number[], string>>(
    () => ({
      labels,
      datasets: [
        {
          ...BAR_STYLE,
          type: 'bar' as const,
          label: 'All Jobs',
          data: buckets.map((bucket) => bucket.allJobsCount),
          backgroundColor: allJobsColor,
        },
        {
          ...BAR_STYLE,
          type: 'bar' as const,
          label: 'CX Won',
          data: buckets.map((bucket) => bucket.cxWonJobsCount),
          backgroundColor: cxWonColor,
        },
        {
          ...BAR_STYLE,
          type: 'bar' as const,
          label: 'OP Won',
          data: buckets.map((bucket) => bucket.opWonJobsCount),
          backgroundColor: opWonColor,
        },
      ],
    }),
    [labels, buckets, allJobsColor, cxWonColor, opWonColor],
  )

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { display: false },
          title: { display: true, text: 'Number of bids received' },
          ticks: { maxRotation: 0, autoSkip: false },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Number of jobs' },
          ticks: { precision: 0 },
        },
      },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { usePointStyle: true, boxWidth: 18, padding: 20 },
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${(context.parsed.y ?? 0).toLocaleString()}`,
          },
        },
      },
    }),
    [],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'bar', number[], string> type='bar' data={data} options={options} />
    </div>
  )
}
